/**
 * Реестр прав. Единственный источник истины для фронта и бэка.
 *
 * CLAUDE.md, правило 14: роли — конфиг, не код. В бизнес-логике не должно быть
 * проверок вида `if (user.role === '...')` — только `can(actor, capability, resource)`.
 *
 * Право отвечает на вопрос ЧТО, область (Scope) — НАСКОЛЬКО ШИРОКО,
 * membership.siteIds — ГДЕ ИМЕННО. Разделение обязательно: без него роли
 * размножаются комбинаторно («механик Далласа», «механик Хьюстона», ...).
 */
/** Область действия права. Порядок значим: none < own < site < org. */
export const SCOPES = ['none', 'own', 'site', 'org'];
const SCOPE_RANK = { none: 0, own: 1, site: 2, org: 3 };
export function scopeRank(scope) {
    return SCOPE_RANK[scope];
}
/** Права с градацией области — отображаются в редакторе ролей матрицей. */
export const SCOPED_CAPABILITIES = [
    'inspection:read',
    'inspection:create',
    'defect:read',
    'defect:create',
    'defect:assign',
    'defect:close',
    'unit:read',
    'unit:write',
    'site:read',
    'site:write',
    'user:read',
    'user:invite',
];
/**
 * Права без градации — блок «особые права», просто вкл/выкл.
 * Технически хранятся с областью 'org' or 'none'.
 */
export const BINARY_CAPABILITIES = [
    'evidence:export',
    'policy:write',
    // Наложить юридическую блокировку должно быть легко, снять — трудно:
    // это разные права, а не одно «управление блокировкой».
    'legal_hold:place',
    'legal_hold:release',
    'retention:configure',
    'retention:purge',
    'trust:read',
    'template:write',
    'threshold:write',
    'audit:read',
    'role:write',
    'user:role:grant',
    'org:settings',
    'org:billing',
    'org:delete',
    'audit:configure',
    'support:impersonate',
];
export const CAPABILITIES = [...SCOPED_CAPABILITIES, ...BINARY_CAPABILITIES];
const BINARY_SET = new Set(BINARY_CAPABILITIES);
export function isBinaryCapability(capability) {
    return BINARY_SET.has(capability);
}
/**
 * Права, которые НИКОГДА не попадают в кастомную роль.
 * Иначе администратор соберёт роль, способную сама себя расширить.
 */
export const PROTECTED_CAPABILITIES = [
    'org:billing',
    'retention:purge',
    'org:delete',
    'audit:configure',
    'support:impersonate',
];
const PROTECTED_SET = new Set(PROTECTED_CAPABILITIES);
export function isProtectedCapability(capability) {
    return PROTECTED_SET.has(capability);
}
/**
 * Сочетания, снимающие разделение обязанностей.
 * CLAUDE.md: предупреждение, не запрет — во флоте на 20 машин один человек делает всё.
 */
export const SEPARATION_OF_DUTY_WARNINGS = [
    {
        pair: ['defect:close', 'defect:assign'],
        message: 'The same person both assigns the repair and verifies it was done.',
    },
    {
        pair: ['evidence:export', 'org:settings'],
        message: 'Evidence export combined with the right to change retention settings.',
    },
    {
        pair: ['role:write', 'user:role:grant'],
        message: 'The same person creates roles and hands them out.',
    },
];
/** Группировка для матрицы в редакторе ролей. */
export const CAPABILITY_GROUPS = [
    {
        resource: 'inspection',
        label: 'Inspections',
        capabilities: ['inspection:read', 'inspection:create'],
    },
    {
        resource: 'defect',
        label: 'Defects',
        capabilities: ['defect:read', 'defect:create', 'defect:assign', 'defect:close'],
    },
    { resource: 'unit', label: 'Units', capabilities: ['unit:read', 'unit:write'] },
    { resource: 'site', label: 'Sites', capabilities: ['site:read', 'site:write'] },
    { resource: 'user', label: 'People', capabilities: ['user:read', 'user:invite'] },
];
export const CAPABILITY_LABELS = {
    'inspection:read': 'View inspections',
    'inspection:create': 'Perform inspections',
    'defect:read': 'View defects',
    'defect:create': 'Report defects',
    'defect:assign': 'Assign a mechanic',
    'defect:close': 'Close defects',
    'unit:read': 'View units',
    'unit:write': 'Edit units',
    'site:read': 'View sites',
    'site:write': 'Edit sites',
    'user:read': 'View people',
    'user:invite': 'Invite people',
    'evidence:export': 'Export evidence package',
    'policy:write': 'Publish inspection policy',
    'legal_hold:place': 'Place legal hold',
    'legal_hold:release': 'Release legal hold',
    'retention:configure': 'Configure retention periods',
    'retention:purge': 'Purge data past retention',
    'trust:read': 'View Trust Score',
    'template:write': 'Edit inspection templates',
    'threshold:write': 'Configure thresholds and rules',
    'audit:read': 'View audit log',
    'role:write': 'Create and edit roles',
    'user:role:grant': 'Grant roles',
    'org:settings': 'Organization settings',
    'org:billing': 'Billing',
    'org:delete': 'Delete organization',
    'audit:configure': 'Configure auditing',
    'support:impersonate': 'Impersonate a user',
};
//# sourceMappingURL=capabilities.js.map